'use client';

import { type JSX, type PropsWithChildren, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Icon } from '@videoblocks/design-system';

/**
 * Props for individual reasoning part rendering
 */
export interface ReasoningPartProps {
    text: string;
    status: { type: string };
}

/**
 * Renders a single reasoning/thinking block content.
 * This is used inside ReasoningGroup or can be used standalone.
 */
export function ReasoningPart({ text }: ReasoningPartProps): JSX.Element {
    return <div className="whitespace-pre-wrap wrap-anywhere text-sm text-gray-600 leading-relaxed">{text}</div>;
}

/**
 * Props for the reasoning group wrapper.
 */
export interface ReasoningGroupProps {
    startIndex: number;
    endIndex: number;
}

/**
 * Collapsible wrapper for reasoning/thinking blocks.
 * Wraps consecutive reasoning parts in an expandable section.
 */
export function ReasoningGroup({ children }: PropsWithChildren<ReasoningGroupProps>): JSX.Element {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div
            className={twMerge(
                'my-3 rounded-lg border border-gray-200 bg-gray-50',
                isExpanded ? 'max-w-[90%]' : 'inline-block',
            )}
        >
            <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className={twMerge(
                    'flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 rounded-lg',
                    isExpanded && 'w-full border-b border-gray-200 rounded-b-none',
                )}
                aria-expanded={isExpanded}
            >
                <Icon name="brain" className="h-4 w-4 shrink-0" />
                <span>Thinking</span>
                <Icon
                    name="chevronDown"
                    className={twMerge(
                        'h-3 w-3 shrink-0 transition-transform text-gray-600',
                        isExpanded && 'rotate-180',
                    )}
                />
            </button>
            {isExpanded && <div className="max-h-64 overflow-y-auto overflow-x-hidden px-3 py-2">{children}</div>}
        </div>
    );
}
